import { useEffect, useState } from 'react';
import { BenefitSection } from './CoverComparison';
import { useDispatch, useSelector } from 'react-redux';
import {
  calculateAge,
  calculateAgeBasedOnCAE,
  formatCurrency,
  getAgeFromLoading,
  getHealthFundImagePath,
  refactorCustomerType,
} from '../utils/helpers';
import axiosRequest from '../utils/axiosConfig';
import { Loader, Select } from '@mantine/core';
import {
  updateFormData,
  updateSalesReqBody,
} from '../features/FormRegistration';
import { CoverType } from '../types/types';
import { toast } from 'react-toastify';
import { ShowIf } from './Helpers/ShowIf';

function CurrentHealthFund({
  expandedBenefits = [],
  onToggleDescription,
}: any) {
  const dispatch = useDispatch();
  const { formData, states, salesReqBody } = useSelector(
    (state: any) => state.formRegistration,
  );
  const { selectedProduct } = useSelector((state: any) => state.compare);
  const [isLoading, setIsLoading] = useState(false);

  const [hospitalBenefits, setHospitalBenefits] = useState<any>([]);
  const [extraBenefits, setExtraBenefits] = useState<any>([]);

  const [productPrice, setProductPrice] = useState<any>(null);
  useEffect(() => {
    if (!selectedProduct) {
      return;
    }
    
    
    setIsLoading(true);
    axiosRequest
      .post(`/customerProductBenefits`, {
        products: [{ product_id: selectedProduct.product_id }],
      })
      .then(async (res) => {
        if (!res.data.data['1']) {
          return toast.error(
            'Error fetching product benefits, No product benefits',
          );
        }
        const responseData = res.data.data['1'].benefits;
        if (responseData) {
          const benefits = responseData;

          setHospitalBenefits(
            benefits
              .filter((b: any) => b.benefit_type === 'hospital')
          );
          setExtraBenefits(
            benefits
              .filter((b: any) => b.benefit_type === 'extra')
          );
        }
      })
      .catch((err) => {
        console.error('Error fetching product benefits:', err);
        toast.error('Error fetching product benefits');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedProduct]);

  useEffect(() => {
    if (selectedProduct) {
      let CDACustomer;
      let CAECustomer;
      let CDAPartner;
      let CAEPartner;
      let customerAge = calculateAgeBasedOnCAE(formData.dateOfBirth?.[0]);
      let partnerAge = calculateAgeBasedOnCAE(formData.dateOfBirth?.[1]);
      if (formData.changedLoadingManuallyCustomer) {
        customerAge = getAgeFromLoading(formData.customerLoading);
      }
      if (formData.changedLoadingManuallyPartner) {
        partnerAge = getAgeFromLoading(formData.partnerLoading);
      }

      if (customerAge > 30) {
        CDACustomer = 30;
        CAECustomer = customerAge;
      } else {
        CDACustomer = customerAge;
        CAECustomer = 30;
      }

      if (customerAge > 18 && customerAge <= 30) {
        CAECustomer = 30;
      }

      if (formData.dateOfBirth?.[1] !== '-') {
        if (partnerAge > 30) {
          CDAPartner = 30;
          CAEPartner = partnerAge;
        } else {
          CDAPartner = partnerAge;
          CAEPartner = 30;
        }
        if (partnerAge > 18 && partnerAge <= 30) {
          CAEPartner = 30;
        }
      }
      if (formData.hadContinuousCover) {
        CAECustomer = 30;
      }
      if (formData.hadContinuousCoverPartner) {
        CAEPartner = 30;
      }
      const selectedState = states.find(
        (state: any) => state.state_name === formData.location,
      );
      setIsLoading(true);
      const priceReqBody = {
        start_date: '2026-04-01',
        state_id: selectedState?.state_id || 1,
        customer_type: refactorCustomerType(formData.coverIsFor),
        rebate_percentage:
          formData.rebatePercentage !== 'No Rebate'
            ? formData.rebatePercentage
            : 0,
        customer: {
          certified_discount_age: Math.floor(CDACustomer), // Ensure integer
          certified_age_of_entry: Math.floor(CAECustomer),
        },
        ...(formData.dateOfBirth?.[1] !== '-' && {
          partner: {
            certified_discount_age: Math.floor(CDAPartner ?? 0), // Ensure integer
            certified_age_of_entry: Math.floor(CAEPartner ?? 0),
          },
        }),
        products: [{ product_id: selectedProduct.product_id }],
        dependants: salesReqBody.dependants.map((dependant: any) => ({
          age: Math.floor(Number.parseInt(dependant.age) || 0),
          university:
            dependant.education_details.full_time === 'Yes' ? true : false,
        })),
      };
      if (salesReqBody.dependants.length === 0) {
        delete priceReqBody.dependants;
      }
      axiosRequest.post('/customerPrices', priceReqBody)
        .then((res) => {
          const paymentFrequency =
            formData.paymentFrequency?.toLowerCase() === 'annual'
              ? 'yearly'
              : formData.paymentFrequency?.toLowerCase();

          const priceValue =
            res.data.data[0]?.prices?.[paymentFrequency]?.price;

          const priceData =
            priceValue !== undefined
              ? priceValue.toFixed(2).toString().split('.')
              : ['0', '00'];

          const coverMainPrice = priceData?.[0];
          const coverMainPriceDecimal = priceData?.[1];
          const lhcComponent =
            res.data.data[0]?.prices?.[paymentFrequency]?.lhc_component;
          const totalCoverMainPrice = (
            Number.parseFloat(`${coverMainPrice}.${coverMainPriceDecimal}`) +
            lhcComponent
          ).toFixed(2);

          setProductPrice({
            coverMainPrice,
            coverMainPriceDecimal,
            lhcComponent,
            totalCoverMainPrice,
          });
        })
        .catch((err) => {
          console.error('Error fetching product price:', err);
          toast.error('Error fetching product price');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    selectedProduct,
    salesReqBody.partner.age,
    salesReqBody.customer.age,
    salesReqBody.payment.payment_period,
    salesReqBody.residential_address.state,
    salesReqBody.payment.payment_method,
    salesReqBody.dependants,
  ]);
  const isValidPrice = (price: string) => {
    return !isNaN(Number.parseFloat(price)) && price.length > 0;
  };
  const { isMobileView, isDesktopView } = useSelector(
    (state: any) => state.viewport,
  );

  if (isMobileView) {
    if (!selectedProduct) {
      return (
        <div className="w-full">
          <div className="rounded-r-[10px] rounded-tl-[10px] w-[min-content] h-[30px] truncate px-[15px] py-[7px]"></div>

          <div className="flex py-[14px] gap-[15px] flex-shrink flex-col border border-[#D1D7DF] h-full items-center w-full">
            <span className="text-[14px]">My current policy</span>
            <div className="border border-dashed border-[#A2AFBE] w-[80%] h-[60px] rounded-[8px]"></div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="w-1/2">
          <div className="rounded-r-[10px] rounded-tl-[10px] w-[min-content] h-[30px] truncate px-[15px] py-[7px]"></div>
          <div className="flex flex-col items-center border border-[#D1D7DF] relative w-full p-[10px] gap-[5px]">
            <span className="text-[14px] text-[#17385D]">
              My current policy
            </span>
            {isLoading ? (
              <div className="flex items-center mt-[50px]">
                <Loader />
              </div>
            ) : (
              <>
                <ShowIf condition={formData.healthFund}>
                  <img
                    src={getHealthFundImagePath(
                      Number.parseInt(formData.healthFund?.split('.')[1]),
                    )}
                    alt="health fund logo"
                    className="w-[80px] h-[40px]"
                  />
                </ShowIf>
                <span className="text-[13px] font-bold w-full text-center leading-tight">
                  {selectedProduct.product_name}
                </span>

                {productPrice && (
                  <>
                    {isValidPrice(`${productPrice?.totalCoverMainPrice}`) ? (
                      <span className="text-[16px] font-medium text-center w-full">
                        <span className="font-[600]">$</span>
                        <span className="text-[22px] font-[600]">
                          {
                            productPrice?.totalCoverMainPrice
                              ?.toString()
                              .split('.')[0]
                          }
                        </span>
                        <span className="font-[600] text-[12px]">
                          .{
                            productPrice?.totalCoverMainPrice
                              ?.toString()
                              .split('.')[1]
                          }
                        </span>
                      </span>
                    ) : (
                      <span className="text-[18px] flex items-center justify-center relative font-medium h-[40px]">
                        Not available
                      </span>
                    )}
                    <Select
                      data={['Fortnightly', 'Monthly', 'Annual']}
                      size="xs"
                      classNames={{
                        input: 'text-center',
                      }}
                      value={formData.paymentFrequency}
                      onChange={(e) => {
                        dispatch(
                          updateFormData({
                            ...formData,
                            paymentFrequency: e,
                          }),
                        );
                        dispatch(
                          updateSalesReqBody({
                            ...salesReqBody,
                            payment: {
                              ...salesReqBody.payment,
                              payment_period: e === 'Annual' ? 'Yearly' : e,
                              payment_date: null,
                            },
                          }),
                        );
                      }}
                    />

                    {(formData.typeOfCover === CoverType.HospitalAndExtras ||
                      formData.typeOfCover === CoverType.Hospital) && (
                      <ShowIf condition={productPrice?.lhcComponent > 0}>
                        <span className="text-start text-[11px]">
                          <strong>
                            {formatCurrency(
                              Number.parseFloat(
                                `${productPrice?.coverMainPrice}.${productPrice?.coverMainPriceDecimal}`,
                              ),
                            )}
                          </strong>{' '}
                          +{' '}
                          <strong>
                            {formatCurrency(productPrice?.lhcComponent)}
                          </strong>{' '}
                          Lifetime Health Cover Loading
                        </span>
                      </ShowIf>
                    )}
                  </>
                )}
                <div className="w-full bg-[#F6F6F6] border-t-[1px] border-b-[1px] border-[#D9D9D9] py-[17px] min-h-[80px] mb-[23px] flex items-center justify-center"></div>

                <div className="flex flex-col w-full">
                  <div className="flex justify-between w-full px-[15px] mb-[19px]">
                    <span className="font-bold text-[12px]">Excess</span>
                    <span className="font-bold text-[12px]">
                      {formatCurrency(
                        Number.parseFloat(selectedProduct.excess),
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between w-full px-[15px] mb-[20px]">
                    <span className="font-bold text-[12px]">Co payment</span>
                    <span className="font-bold text-[12px]">
                      {selectedProduct.coPayment}
                    </span>
                  </div>
                </div>
                <div className="border-t-2 w-full border-[#D1D7DF] mb-[10px]"></div>
                <BenefitSection
                  title="Hospital Benefits"
                  benefits={hospitalBenefits}
                  expandedBenefits={expandedBenefits}
                  onToggle={onToggleDescription}
                />
                <div className="border-t-2 w-full border-[#D1D7DF] mt-4"></div>
                <BenefitSection
                  title="Extras Cover"
                  benefits={extraBenefits}
                  expandedBenefits={expandedBenefits}
                  onToggle={onToggleDescription}
                />
                <div className="border-t-2 w-full border-[#D1D7DF] mt-4"></div>
                {selectedProduct.ambulance && (
                  <div className="flex flex-col px-[15px] text-start items-start w-full mb-[36px]">
                    <span className="text-[17px] font-medium mb-[10px]">
                      Ambulance Cover
                    </span>
                    <p
                      className="text-[12px]"
                      dangerouslySetInnerHTML={{
                        __html: selectedProduct.ambulance,
                      }}></p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }
  }

  if (isDesktopView) {
    if (!selectedProduct) {
      return (
        <div className="w-full">
          <div className="rounded-r-[10px] rounded-tl-[10px] w-[min-content] h-[30px] truncate px-[15px] py-[7px]"></div>

          <div className="flex py-[14px] gap-[15px] flex-shrink flex-col border border-[#D1D7DF] h-full items-center w-full">
            <span className="text-[14px]">My current policy</span>
            <div className="border border-dashed border-[#A2AFBE] w-[80%] h-[60px] rounded-[8px]"></div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="w-full">
          <div className="rounded-r-[10px] rounded-tl-[10px] w-[min-content] h-[30px] truncate px-[15px] py-[7px]"></div>
          <div className="flex flex-col items-center border border-[#D1D7DF] relative w-full p-[10px] md:gap-[8px] gap-[5px] pb-4">
            <span className="text-[14px] text-[#17385D]">
              My current policy
            </span>
            {isLoading ? (
              <div className="flex items-center mt-[50px]">
                <Loader />
              </div>
            ) : (
              <>
                <ShowIf condition={formData.healthFund}>
                  <img
                    src={getHealthFundImagePath(
                      Number.parseInt(formData.healthFund?.split('.')[1]),
                    )}
                    alt="health fund logo"
                    className="h-[50px]"
                  />
                </ShowIf>
                <span className="text-[14px] font-bold w-full min-h-[40px] text-center">
                  {selectedProduct.product_name}
                </span>

                {productPrice && (
                  <>
                    {isValidPrice(`${productPrice?.totalCoverMainPrice}`) ? (
                      <span className="text-[28px] relative font-bold text-center w-full">
                        {formatCurrency(productPrice?.totalCoverMainPrice)}
                      </span>
                    ) : (
                      <span className="text-[24px] flex items-center justify-center relative font-medium h-[50px]">
                        Not available
                      </span>
                    )}
                    <Select
                      data={['Fortnightly', 'Monthly', 'Annual']}
                      className="w-full"
                      classNames={{
                        input: 'text-center',
                      }}
                      value={formData.paymentFrequency}
                      onChange={(e) => {
                        dispatch(
                          updateFormData({
                            ...formData,
                            paymentFrequency: e,
                          }),
                        );
                        dispatch(
                          updateSalesReqBody({
                            ...salesReqBody,
                            payment: {
                              ...salesReqBody.payment,
                              payment_period: e === 'Annual' ? 'Yearly' : e,
                              payment_date: null,
                            },
                          }),
                        );
                      }}
                    />

                    {(formData.typeOfCover === CoverType.HospitalAndExtras ||
                      formData.typeOfCover === CoverType.Hospital) && (
                      <ShowIf condition={productPrice?.lhcComponent > 0}>
                        <span className="text-start text-[12px]">
                          <strong>
                            {formatCurrency(
                              Number.parseFloat(
                                `${productPrice?.coverMainPrice}.${productPrice?.coverMainPriceDecimal}`,
                              ),
                            )}
                          </strong>{' '}
                          +{' '}
                          <strong>
                            {formatCurrency(productPrice?.lhcComponent)}
                          </strong>{' '}
                          Lifetime Health Cover Loading
                        </span>
                      </ShowIf>
                    )}
                  </>
                )}
                <div className="relative w-full mb-[10px]">
                  <div className="w-full bg-[#F6F6F6] border-t-[1px] border-b-[1px] border-[#D9D9D9] py-[10px] min-h-[80px] flex items-center justify-center"></div>
                </div>

                <div className="flex flex-col w-full">
                  <div className="flex justify-between w-full px-[15px] mb-[19px]">
                    <span className="font-bold text-[14px]">Excess</span>
                    <span className="font-bold text-[14px]">
                      {formatCurrency(
                        Number.parseFloat(selectedProduct.excess),
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between w-full px-[15px] mb-[20px]">
                    <span className="font-bold text-[14px]">Co payment</span>
                    <span className="font-bold text-[14px]">
                      {selectedProduct.coPayment}
                    </span>
                  </div>
                </div>
                <div className="border-t-2 w-full border-[#D1D7DF]"></div>
                <div className="min-h-[35px] w-full"></div>

                <BenefitSection
                  title="Hospital Benefits"
                  benefits={hospitalBenefits}
                  expandedBenefits={expandedBenefits}
                  onToggle={onToggleDescription}
                />
                <div className="border-t-2 w-full border-[#D1D7DF] mt-8"></div>
                <BenefitSection
                  title="Extras Cover"
                  benefits={extraBenefits}
                  expandedBenefits={expandedBenefits}
                  onToggle={onToggleDescription}
                />
                <div className="border-t-2 w-full border-[#D1D7DF] mt-8"></div>
                {selectedProduct.ambulance && (
                  <div className="flex flex-col px-[15px] text-start items-start w-full mb-[36px]">
                    <span className="text-[17px] font-medium mb-[10px]">
                      Ambulance Cover
                    </span>
                    <p
                      className="text-[12px]"
                      dangerouslySetInnerHTML={{
                        __html: selectedProduct.ambulance,
                      }}></p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }
  }
}

export default CurrentHealthFund;
